import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
  Alert,
  Button,
  Chip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckIcon from "@mui/icons-material/Check";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"; // Question mark icon
import ArrowBackIcon from "@mui/icons-material/ArrowBack"; // Back arrow icon
import AccessTimeIcon from "@mui/icons-material/AccessTime"; // Clock icon
import { styled } from "@mui/material/styles";
import { API_URL } from "../utils/config";
import {
  findBestMatchingFAQ,
  getGeminiAnswer,
  processAttachment,
  verifyGeminiApiKey,
  getSuggestedQuestions,
  getDirectAnswerByQuestion,
} from "../utils/semanticMatching";

const ChatContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: "20px",
  right: "20px",
  zIndex: 1000,
  transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
  "@media (max-width: 600px)": {
    bottom: "10px",
    right: "10px",
  },
}));

// Enhanced styling for the chat widget
const ChatWidget = styled(Paper)(({ theme }) => ({
  width: "350px",
  height: "500px",
  display: "flex",
  flexDirection: "column",
  borderRadius: "20px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
  overflow: "hidden",
  animation: "slideIn 0.5s ease-out",
  backdropFilter: "blur(10px)",
  background: "rgba(255, 255, 255, 0.95)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  "@keyframes slideIn": {
    "0%": {
      transform: "translateY(100%) scale(0.8)",
      opacity: 0,
    },
    "100%": {
      transform: "translateY(0) scale(1)",
      opacity: 1,
    },
  },
  "@media (max-width: 600px)": {
    width: "300px",
    height: "450px",
  },
}));

// Enhanced header with gradient
const ChatHeader = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #E1BA66 0%, #D4A94C 100%)",
  color: "#2C1810",
  padding: "16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxShadow: "0 2px 8px rgba(225, 186, 102, 0.2)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)",
    animation: "shine 3s infinite",
  },
  "@keyframes shine": {
    "0%": {
      transform: "translateX(-100%)",
    },
    "100%": {
      transform: "translateX(100%)",
    },
  },
}));

const ChatMessages = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "16px",
  overflowY: "auto",
  backgroundColor: "#f8f9fa",
  backgroundImage:
    "radial-gradient(circle at 1px 1px, #e0e0e0 1px, transparent 0)",
  backgroundSize: "20px 20px",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#E1BA66",
    borderRadius: "3px",
  },
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isUser",
})(({ theme, isUser }) => ({
  maxWidth: "80%",
  marginBottom: "12px",
  padding: "12px 16px",
  borderRadius: "18px",
  backgroundColor: isUser ? "#E1BA66" : "#FFF9F0",
  color: isUser ? "#2C1810" : "#333",
  alignSelf: isUser ? "flex-end" : "flex-start",
  marginLeft: isUser ? "auto" : "0",
  boxShadow: isUser
    ? "0 2px 8px rgba(225, 186, 102, 0.2)"
    : "0 2px 8px rgba(0, 0, 0, 0.05)",
  animation: "fadeIn 0.3s ease-out",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "18px",
    background: isUser
      ? "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)"
      : "linear-gradient(135deg, rgba(225,186,102,0.05) 0%, transparent 100%)",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover::before": {
    opacity: 1,
  },
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
      transform: "translateY(10px) scale(0.95)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0) scale(1)",
    },
  },
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    transform: "translateY(-1px)",
    transition: "all 0.2s ease",
  },
}));

const ChatInput = styled(Box)(({ theme }) => ({
  padding: "16px",
  backgroundColor: "white",
  borderTop: "1px solid #e0e0e0",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.05)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: "linear-gradient(90deg, transparent, #E1BA66, transparent)",
  },
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "8px 16px",
  backgroundColor: "white",
  borderRadius: "18px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  alignSelf: "flex-start",
  marginBottom: "12px",
  animation: "pulse 1.5s infinite",
  "@keyframes pulse": {
    "0%": {
      opacity: 0.6,
    },
    "50%": {
      opacity: 1,
    },
    "100%": {
      opacity: 0.6,
    },
  },
}));

const Dot = styled(Box)(({ theme, delay }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "#E1BA66",
  animation: `bounce 1.4s infinite ${delay}ms`,
  "@keyframes bounce": {
    "0%, 80%, 100%": {
      transform: "scale(0)",
    },
    "40%": {
      transform: "scale(1)",
    },
  },
}));

const FilePreview = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  backgroundColor: "#f0f0f0",
  borderRadius: "8px",
  marginBottom: "8px",
  border: "1px solid #e0e0e0",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#e8e8e8",
    transform: "translateY(-1px)",
  },
}));

const SuggestionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginLeft: "8px",
  marginTop: "8px",
  marginBottom: "16px",
}));

const SuggestionChip = styled(Box)(({ theme }) => ({
  background: "#E1BA66",
  color: "#2C1810",
  border: "none",
  padding: "8px 12px",
  borderRadius: "20px",
  cursor: "pointer",
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  fontSize: "0.85rem",
  transition: "all 0.3s ease",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  "&:hover": {
    background: "#d4a94c",
    transform: "scale(1.05)",
    boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
  },
}));

const Chatbot = () => {
  // Separate states for chatbot and query interfaces
  const [isOpen, setIsOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]); // For chatbot
  const [queryMessages, setQueryMessages] = useState([]); // For query interface
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [apiStatus, setApiStatus] = useState("checking");
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [usedSuggestions, setUsedSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [queryFormData, setQueryFormData] = useState({
    name: "",
    email: "",
    question: "",
  });
  const [querySubmitted, setQuerySubmitted] = useState(false);
  const [queryError, setQueryError] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [previousQueries, setPreviousQueries] = useState([]);
  const [showPreviousQueries, setShowPreviousQueries] = useState(false);
  const [viewingPreviousQueries, setViewingPreviousQueries] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);

  useEffect(() => {
    // Load suggestions
    loadSuggestions();

    // Verify API key
    verifyApiKey();
  }, []);

  const loadSuggestions = () => {
    // Get 3 suggested questions that haven't been used yet
    const allSuggestions = getSuggestedQuestions(10);
    const filteredSuggestions = allSuggestions
      .filter((question) => !usedSuggestions.includes(question))
      .slice(0, 3);

    setSuggestedQuestions(filteredSuggestions);
  };

  const verifyApiKey = async () => {
    try {
      const result = await verifyGeminiApiKey();
      setApiStatus(result.status);
      if (result.status !== "valid") {
        console.error("API Key Issue:", result.message);
      } else {
        console.log("API Key verified successfully");
      }
    } catch (error) {
      console.error("API Verification Error:", error);
      setApiStatus("error");
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("chatMessages", JSON.stringify(chatMessages));
    }
  }, [chatMessages]);

  // Initialize welcome message when chatbot opens
  useEffect(() => {
    if (isOpen && chatMessages.length === 0) {
      setChatMessages([
        {
          text: "Welcome to BharatFLP! I'm your AI assistant. How can I help you today?",
          isUser: false,
          timestamp: new Date().toISOString(),
        },
      ]);
      // Load initial suggestions
      loadSuggestions();
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Show uploading state
      setChatMessages((prev) => [
        ...prev,
        {
          text: `Uploading file: ${file.name}`,
          isUser: true,
          timestamp: new Date().toISOString(),
        },
      ]);

      setIsLoading(true);
      setIsTyping(true);

      try {
        // Process the attachment
        const fileAnalysis = await processAttachment(file);

        if (fileAnalysis) {
          // Update message to include the file
          setChatMessages((prev) => {
            const newMessages = [...prev];
            // Replace the last message with actual file data
            newMessages[newMessages.length - 1] = {
              text: `Attached file: ${file.name}`,
              isUser: true,
              file: file,
              timestamp: new Date().toISOString(),
            };

            // Add AI response about the file
            newMessages.push({
              text: `I've analyzed your file "${fileAnalysis.fileName}" (${fileAnalysis.fileSize}). ${fileAnalysis.analysis}`,
              isUser: false,
              timestamp: new Date().toISOString(),
            });

            return newMessages;
          });
        }
      } catch (error) {
        console.error("Error processing file:", error);
        setChatMessages((prev) => [
          ...prev,
          {
            text: "I'm sorry, I couldn't process your file. Could you try again or send a different file?",
            isUser: false,
            timestamp: new Date().toISOString(),
          },
        ]);
      } finally {
        setIsLoading(false);
        setIsTyping(false);
        setSelectedFile(null);

        // Refresh suggestions after file processing
        loadSuggestions();
        setShowSuggestions(true);
      }
    }
  };

  const handleSuggestionClick = async (question) => {
    // First, add the question to the used suggestions
    setUsedSuggestions((prev) => [...prev, question]);

    // Show the selected question as a user message
    const userMessage = {
      text: question,
      isUser: true,
      timestamp: new Date().toISOString(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setIsTyping(true);

    try {
      // Try to get a direct answer first (faster)
      const directAnswer = getDirectAnswerByQuestion(question);

      if (directAnswer) {
        // Small delay to simulate typing
        setTimeout(() => {
          setChatMessages((prev) => [
            ...prev,
            {
              text: directAnswer.answer,
              isUser: false,
              timestamp: new Date().toISOString(),
              category: directAnswer.category,
              question: directAnswer.question,
              source: "faq",
            },
          ]);

          setIsLoading(false);
          setIsTyping(false);

          // Load new suggestions after answering
          loadSuggestions();
          setShowSuggestions(true);
        }, 500);
      } else {
        // No direct match, try the regular search
        const faqMatch = await findBestMatchingFAQ(question);

        setTimeout(() => {
          if (faqMatch) {
            setChatMessages((prev) => [
              ...prev,
              {
                text: faqMatch.answer,
                isUser: false,
                timestamp: new Date().toISOString(),
                category: faqMatch.category,
                question: faqMatch.question,
                source: "faq",
              },
            ]);
          } else {
            // Fallback to Gemini
            getGeminiAnswer(question)
              .then((response) => {
                setChatMessages((prev) => [
                  ...prev,
                  {
                    text: response,
                    isUser: false,
                    timestamp: new Date().toISOString(),
                    source: "gemini",
                  },
                ]);
              })
              .catch((error) => {
                console.error("Error getting answer:", error);
                setChatMessages((prev) => [
                  ...prev,
                  {
                    text: "I'm sorry, I couldn't generate a response for this question. Please try another question about the Financial Literacy Program.",
                    isUser: false,
                    timestamp: new Date().toISOString(),
                    source: "error",
                  },
                ]);
              });
          }

          setIsLoading(false);
          setIsTyping(false);

          // Load new suggestions
          loadSuggestions();
          setShowSuggestions(true);
        }, 800);
      }
    } catch (error) {
      console.error("Error processing suggestion:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          text: "I'm sorry, I couldn't process your question. Please try again with a different question.",
          isUser: false,
          timestamp: new Date().toISOString(),
          source: "error",
        },
      ]);

      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() && !selectedFile) return;

    const userMessage = {
      text: input,
      isUser: true,
      timestamp: new Date().toISOString(),
      file: selectedFile,
    };

    setInput("");
    setSelectedFile(null);
    setChatMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setIsTyping(true);
    setShowSuggestions(false);

    try {
      const faqMatch = await findBestMatchingFAQ(input);

      setTimeout(async () => {
        if (faqMatch) {
          setChatMessages((prev) => [
            ...prev,
            {
              text: faqMatch.answer,
              isUser: false,
              timestamp: new Date().toISOString(),
              category: faqMatch.category,
              question: faqMatch.question,
              source: "faq",
            },
          ]);
        } else {
          try {
            const geminiResponse = await getGeminiAnswer(input);
            setChatMessages((prev) => [
              ...prev,
              {
                text: geminiResponse,
                isUser: false,
                timestamp: new Date().toISOString(),
                source: "gemini",
              },
            ]);
          } catch (error) {
            console.error("Gemini API error:", error);
            setChatMessages((prev) => [
              ...prev,
              {
                text: "I'm sorry, I couldn't generate a response at the moment. Please try again with a question about the Financial Literacy Program.",
                isUser: false,
                timestamp: new Date().toISOString(),
                source: "error",
              },
            ]);
          }
        }

        loadSuggestions();
        setShowSuggestions(true);
        setIsLoading(false);
        setIsTyping(false);
      }, 500);
    } catch (error) {
      console.error("Chat Processing Error:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          text: "Sorry, I encountered an error processing your request. Please try again later.",
          isUser: false,
          timestamp: new Date().toISOString(),
          source: "error",
        },
      ]);
      setIsLoading(false);
      setIsTyping(false);
      loadSuggestions();
      setShowSuggestions(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toggleQueryForm = () => {
    if (showQueryForm) {
      setShowQueryForm(false);
    } else {
      setShowQueryForm(true);
      setIsOpen(false);
    }
  };

  const handleQueryInputChange = (e) => {
    const { name, value } = e.target;
    setQueryFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleQuerySubmit = async (e) => {
    if (e) e.preventDefault();

    if (
      !queryFormData.question ||
      !queryFormData.name ||
      !queryFormData.email
    ) {
      setQueryError("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    setQueryError(null);

    // Create the user's query message
    const userMessage = {
      text: queryFormData.question,
      isUser: true,
      timestamp: new Date().toISOString(),
      type: "query",
    };

    // Add the user's message to the chat
    setQueryMessages((prev) => [...prev, userMessage]);

    try {
      const userId = localStorage.getItem("userId");
      const userType = localStorage.getItem("type");

      const response = await fetch(
        `${API_URL}/admin/query-master`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("token"),
          },
          body: JSON.stringify({
            title: queryFormData.question,
            description: queryFormData.question,
            studentID: userId,
            createdBy: userId,
            email: queryFormData.email,
            name: queryFormData.name,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Add the confirmation message with status
        setQueryMessages((prev) => [
          ...prev,
          {
            text: "Thank you for your question. Our support team will review it and get back to you soon. You'll see the response here when it's available.",
            isUser: false,
            timestamp: new Date().toISOString(),
            source: "system",
            queryId: data._id || data.id,
            type: "query-confirmation",
            status: "open",
          },
        ]);

        // Only clear the question field, keep name and email
        setQueryFormData((prev) => ({
          ...prev,
          question: "",
        }));

        setQuerySubmitted(true);
      } else {
        setQueryError(
          data.error || "Failed to submit your question. Please try again."
        );
        setQueryMessages((prev) => [
          ...prev,
          {
            text: "Failed to submit your question. Please try again.",
            isUser: false,
            timestamp: new Date().toISOString(),
            source: "error",
            status: "error",
          },
        ]);
      }
    } catch (error) {
      console.error("Error submitting query:", error);
      setQueryError(
        "Network error. Please check your connection and try again."
      );
      setQueryMessages((prev) => [
        ...prev,
        {
          text: "Network error. Please check your connection and try again.",
          isUser: false,
          timestamp: new Date().toISOString(),
          source: "error",
          status: "error",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPreviousQueries = async () => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        setShowPreviousQueries(false);
        setIsLoading(false);
        return;
      }

      const response = await fetch(
        `${API_URL}/admin/query-master/student/${userId}`,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreviousQueries(data);
        setShowPreviousQueries(true);
        setViewingPreviousQueries(true); // Show the previous queries interface

        // Ensure we're not affecting the input field
        setQueryFormData((prev) => ({
          ...prev,
          // Keep the existing values but don't modify the question field
          name: prev.name,
          email: prev.email,
        }));
      } else {
        console.error("Failed to fetch previous queries");
        setPreviousQueries([]);
      }
    } catch (error) {
      console.error("Error fetching previous queries:", error);
      setPreviousQueries([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuerySelect = (query) => {
    setSelectedQuery(query);
    setViewingPreviousQueries(true); // Ensure we're in the previous queries view mode
  };

  const handleBackToQueryForm = () => {
    setViewingPreviousQueries(false);
    setSelectedQuery(null);
  };

  useEffect(() => {
    // Pre-fill user information if available
    const userId = localStorage.getItem("userId");
    const userEmail = localStorage.getItem("email");
    const firstName = localStorage.getItem("firstName");
    const lastName = localStorage.getItem("lastName");

    if (userEmail || firstName) {
      setQueryFormData((prev) => ({
        ...prev,
        email: userEmail || prev.email,
        name:
          firstName && lastName
            ? `${firstName} ${lastName}`
            : firstName || prev.name,
      }));
    }
  }, []);

  // Add this useEffect to load existing queries when the component mounts
  useEffect(() => {
    const loadExistingQueries = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const response = await fetch(
          `${API_URL}/admin/query-master/student/${userId}`,
          {
            headers: {
              Authorization: localStorage.getItem("token"),
            },
          }
        );

        if (response.ok) {
          const queries = await response.json();

          // Convert queries to message format and add to queryMessages
          const queryMessages = queries
            .map((query) => [
              // User's query message
              {
                text: query.description,
                isUser: true,
                timestamp: query.createdAt,
                type: "query",
                queryId: query._id, // Add queryId to user message
              },
              // System's confirmation message
              {
                text: "Thank you for your question. Our support team will review it and get back to you soon. You'll see the response here when it's available.",
                isUser: false,
                timestamp: query.createdAt,
                source: "system",
                queryId: query._id,
                type: "query-confirmation",
                status: query.status || "open",
              },
              // If there's a response, add it
              ...(query.response
                ? [
                    {
                      text: query.response,
                      isUser: false,
                      timestamp: query.updatedAt,
                      source: "admin",
                      queryId: query._id,
                      type: "query-response",
                      status: query.status,
                    },
                  ]
                : []),
            ])
            .flat();

          setQueryMessages(queryMessages);
        }
      } catch (error) {
        console.error("Error loading existing queries:", error);
      }
    };

    if (showQueryForm) {
      loadExistingQueries();
    }
  }, [showQueryForm]);

  // Add this useEffect to save messages to localStorage whenever they change
  useEffect(() => {
    if (queryMessages.length > 0) {
      localStorage.setItem("queryMessages", JSON.stringify(queryMessages));
    }
  }, [queryMessages]);

  // Modify the fetchQueryResponses function to prevent duplicates
  const fetchQueryResponses = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const response = await fetch(
        `${API_URL}/admin/query-master/student/${userId}`,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );

      if (response.ok) {
        const queries = await response.json();

        // Process each query to see if it has a response or status update
        queries.forEach((query) => {
          // Find the existing query message
          const existingQueryMessage = queryMessages.find(
            (msg) => msg.queryId === query._id && msg.type === "query"
          );

          if (existingQueryMessage) {
            // Update the existing message with new status and response
            setQueryMessages(
              (prev) =>
                prev
                  .map((msg) => {
                    if (msg.queryId === query._id) {
                      // If it's the query message itself, just update the status
                      if (msg.type === "query") {
                        return {
                          ...msg,
                          status: query.status,
                        };
                      }
                      // If it's a response message, update or remove it
                      if (msg.type === "query-response") {
                        if (query.response) {
                          return {
                            ...msg,
                            text: query.response,
                            status: query.status,
                            timestamp: new Date().toISOString(),
                          };
                        } else {
                          return null; // Remove the response message if there's no response
                        }
                      }
                      // If it's a status update message, update or remove it
                      if (msg.type === "status-update") {
                        if (query.status === "in-progress") {
                          return {
                            ...msg,
                            status: query.status,
                            timestamp: new Date().toISOString(),
                          };
                        } else {
                          return null; // Remove the status update if not in progress
                        }
                      }
                    }
                    return msg;
                  })
                  .filter(Boolean) // Remove null messages
            );

            // Add new response if it doesn't exist
            if (query.response) {
              const hasResponse = queryMessages.some(
                (msg) =>
                  msg.queryId === query._id && msg.type === "query-response"
              );
              if (!hasResponse) {
                setQueryMessages((prev) => [
                  ...prev,
                  {
                    text: query.response,
                    isUser: false,
                    timestamp: new Date().toISOString(),
                    source: "admin",
                    queryId: query._id,
                    type: "query-response",
                    status: query.status,
                  },
                ]);
              }
            }

            // Add status update if needed
            if (query.status === "in-progress") {
              const hasStatusUpdate = queryMessages.some(
                (msg) =>
                  msg.queryId === query._id &&
                  msg.type === "status-update" &&
                  msg.status === "in-progress"
              );
              if (!hasStatusUpdate) {
                setQueryMessages((prev) => [
                  ...prev,
                  {
                    text: "Your query is now being processed by our support team.",
                    isUser: false,
                    timestamp: new Date().toISOString(),
                    source: "system",
                    queryId: query._id,
                    type: "status-update",
                    status: "in-progress",
                  },
                ]);
              }
            }
          }
        });
      }
    } catch (error) {
      console.error("Error fetching query responses:", error);
    }
  };

  // Add this useEffect to periodically check for new responses
  useEffect(() => {
    // Only run this if the chat is open and we're in query mode
    if (!showQueryForm) return;

    // Fetch responses immediately
    fetchQueryResponses();

    // Then set up an interval to check periodically (every 15 seconds)
    const intervalId = setInterval(fetchQueryResponses, 15000);

    return () => clearInterval(intervalId); // Clean up on unmount
  }, [showQueryForm, queryMessages]);

  return (
    <ChatContainer>
      {!isOpen ? (
        <Box sx={{ position: "relative" }}>
          {/* Tooltip-like message with enhanced animations */}
          <Box
            className="hover-message"
            sx={{
              position: "absolute",
              bottom: "80px",
              right: "0",
              background: "linear-gradient(135deg, #ffffff 0%, #f9f3e5 100%)",
              color: "#2C1810",
              padding: "12px 20px",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(212, 169, 76, 0.25)",
              fontSize: "0.9rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              opacity: 0,
              transform: "translateY(20px) scale(0.8)",
              transformOrigin: "bottom right",
              transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)", // Bouncy effect
              pointerEvents: "none",
              zIndex: 1000,
              "&.hover-visible": {
                opacity: 1,
                transform: "translateY(0) scale(1)",
                pointerEvents: "auto",
              },
            }}
          >
            <Typography component="p">
              {showQueryForm ? "Submit a question" : "How may I help you?"}
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#E1BA66",
                  marginLeft: "4px",
                  animation: "pulseAndFade 1.5s infinite",
                  "@keyframes pulseAndFade": {
                    "0%, 100%": { opacity: 0.2, transform: "scale(0.8)" },
                    "50%": { opacity: 1, transform: "scale(1.2)" },
                  },
                }}
              />
            </Typography>
          </Box>

          {/* Chat/Query toggle buttons */}
          <Box sx={{ display: "flex", gap: "10px" }}>
            <IconButton
              onClick={() => {
                setShowQueryForm(false);
                setIsOpen(true);
              }}
              sx={{
                width: "56px",
                height: "56px",
                backgroundColor: "#E1BA66",
                color: "#2C1810",
                boxShadow: "0 4px 20px rgba(225, 186, 102, 0.3)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: "#d4a94c",
                  transform: "translateY(-5px)",
                  boxShadow: "0 6px 25px rgba(225, 186, 102, 0.4)",
                },
              }}
              onMouseEnter={() => {
                const hoverMessage = document.querySelector(".hover-message");
                if (hoverMessage) {
                  hoverMessage.classList.add("hover-visible");

                  // Add typewriter effect when visible
                  const text = hoverMessage.querySelector("p") || hoverMessage;
                  text.style.width = "0";
                  text.style.overflow = "hidden";
                  text.style.whiteSpace = "nowrap";
                  text.style.transition = "width 0.6s steps(20, end)";
                  setTimeout(() => {
                    text.style.width = "100%";
                  }, 100);
                }
              }}
              onMouseLeave={() => {
                const hoverMessage = document.querySelector(".hover-message");
                if (hoverMessage) {
                  hoverMessage.classList.remove("hover-visible");
                }
              }}
            >
              <Box
                className="inner-icon"
                sx={{
                  transition: "transform 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ChatIcon />
              </Box>
            </IconButton>

            <IconButton
              onClick={toggleQueryForm}
              sx={{
                width: "56px",
                height: "56px",
                backgroundColor: showQueryForm ? "#d4a94c" : "#E1BA66",
                color: "#2C1810",
                boxShadow: "0 4px 20px rgba(225, 186, 102, 0.3)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: "#d4a94c",
                  transform: "translateY(-5px)",
                  boxShadow: "0 6px 25px rgba(225, 186, 102, 0.4)",
                },
              }}
            >
              <Box
                className="inner-icon"
                sx={{
                  transition: "transform 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Use the question mark icon */}
                <HelpOutlineIcon />
              </Box>
            </IconButton>
          </Box>

          {/* Query Form */}
          {showQueryForm && (
            <ChatWidget
              sx={{
                position: "absolute",
                bottom: "70px",
                right: "0",
                zIndex: 1000,
                width: "350px",
                maxHeight: "500px",
              }}
            >
              <ChatHeader>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    textShadow: "0 2px 4px rgba(44, 24, 16, 0.1)",
                    animation: "glow 2s ease-in-out infinite",
                    "@keyframes glow": {
                      "0%, 100%": {
                        textShadow: "0 2px 4px rgba(44, 24, 16, 0.1)",
                      },
                      "50%": {
                        textShadow: "0 2px 8px rgba(44, 24, 16, 0.2)",
                      },
                    },
                  }}
                >
                  {viewingPreviousQueries
                    ? selectedQuery
                      ? "Query Details"
                      : "Your Previous Queries"
                    : "BharatFLP Support"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {viewingPreviousQueries && (
                    <IconButton
                      onClick={handleBackToQueryForm}
                      sx={{
                        color: "white",
                        mr: 1,
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "scale(1.1)",
                          backgroundColor: "rgba(255, 255, 255, 0.2)",
                        },
                      }}
                    >
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => setShowQueryForm(false)}
                    sx={{
                      color: "white",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "rotate(90deg) scale(1.1)",
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </ChatHeader>

              {/* Previous Queries View */}
              {viewingPreviousQueries && !selectedQuery && (
                <Box
                  sx={{
                    flex: 1,
                    padding: "16px",
                    overflowY: "auto",
                    backgroundColor: "#f8f9fa",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {isLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress size={24} sx={{ color: "#E1BA66" }} />
                    </Box>
                  ) : previousQueries.length > 0 ? (
                    previousQueries.map((query, index) => (
                      <Paper
                        key={index}
                        elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          borderLeft: `4px solid ${
                            query.status === "resolved"
                              ? "#4caf50"
                              : query.status === "in-progress"
                              ? "#2196f3"
                              : "#ff9800"
                          }`,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          },
                        }}
                        onClick={() => handleQuerySelect(query)}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, color: "#2C1810" }}
                          >
                            {query.title.length > 30
                              ? query.title.substring(0, 30) + "..."
                              : query.title}
                          </Typography>
                          <Chip
                            label={query.status}
                            size="small"
                            sx={{
                              height: "20px",
                              fontSize: "0.6rem",
                              backgroundColor:
                                query.status === "resolved"
                                  ? "#e8f5e9"
                                  : query.status === "in-progress"
                                  ? "#e3f2fd"
                                  : "#fff3e0",
                              color:
                                query.status === "resolved"
                                  ? "#2e7d32"
                                  : query.status === "in-progress"
                                  ? "#1565c0"
                                  : "#ef6c00",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "#555", mb: 1 }}
                        >
                          {query.description.length > 60
                            ? query.description.substring(0, 60) + "..."
                            : query.description}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            {new Date(query.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: query.response ? "#1565c0" : "#ff9800",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {query.response ? (
                              <>
                                <CheckIcon fontSize="inherit" />
                                Answered
                              </>
                            ) : (
                              <>
                                <AccessTimeIcon fontSize="inherit" />
                                Pending
                              </>
                            )}
                          </Typography>
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        p: 3,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#666", textAlign: "center", mb: 2 }}
                      >
                        No previous queries found
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleBackToQueryForm}
                        sx={{
                          borderColor: "#E1BA66",
                          color: "#2C1810",
                          "&:hover": {
                            borderColor: "#d4a94c",
                            backgroundColor: "rgba(225, 186, 102, 0.1)",
                          },
                        }}
                      >
                        Ask a question
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Query Details View */}
              {viewingPreviousQueries && selectedQuery && (
                <Box
                  sx={{
                    flex: 1,
                    padding: "16px",
                    overflowY: "auto",
                    backgroundColor: "#f8f9fa",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      borderLeft: `4px solid ${
                        selectedQuery.status === "resolved"
                          ? "#4caf50"
                          : selectedQuery.status === "in-progress"
                          ? "#2196f3"
                          : "#ff9800"
                      }`,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "#2C1810" }}
                      >
                        Your Query
                      </Typography>
                      <Chip
                        label={selectedQuery.status}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          backgroundColor:
                            selectedQuery.status === "resolved"
                              ? "#e8f5e9"
                              : selectedQuery.status === "in-progress"
                              ? "#e3f2fd"
                              : "#fff3e0",
                          color:
                            selectedQuery.status === "resolved"
                              ? "#2e7d32"
                              : selectedQuery.status === "in-progress"
                              ? "#1565c0"
                              : "#ef6c00",
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: "#555", mb: 2, whiteSpace: "pre-wrap" }}
                    >
                      {selectedQuery.description}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Submitted on:{" "}
                        {new Date(selectedQuery.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        ID: {selectedQuery._id?.substring(0, 8)}
                      </Typography>
                    </Box>
                  </Paper>

                  {selectedQuery.response ? (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        borderLeft: "4px solid #4caf50",
                        backgroundColor: "#f5f9f5",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "#2e7d32",
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CheckIcon fontSize="small" />
                        Response from Support Team
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#333", mb: 1, whiteSpace: "pre-wrap" }}
                      >
                        {selectedQuery.response}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Responded on:{" "}
                        {selectedQuery.updatedAt
                          ? new Date(
                              selectedQuery.updatedAt
                            ).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                    </Paper>
                  ) : (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        borderLeft: "4px solid #ff9800",
                        backgroundColor: "#fff8e1",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "#ef6c00",
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <AccessTimeIcon fontSize="small" />
                        Awaiting Response
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#555" }}>
                        Our support team will respond to your query as soon as
                        possible. Thank you for your patience.
                      </Typography>
                    </Paper>
                  )}

                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleBackToQueryForm}
                      startIcon={<ArrowBackIcon />}
                      sx={{
                        borderColor: "#E1BA66",
                        color: "#2C1810",
                        "&:hover": {
                          borderColor: "#d4a94c",
                          backgroundColor: "rgba(225, 186, 102, 0.1)",
                        },
                      }}
                    >
                      Back to Query Form
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Regular Query Form */}
              {!viewingPreviousQueries && (
                <Box
                  className="query-messages-container"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Messages area with its own scrolling */}
                  <Box
                    sx={{
                      padding: "16px",
                      overflowY: "auto",
                      backgroundColor: "#f8f9fa",
                      backgroundImage:
                        "radial-gradient(circle at 1px 1px, #e0e0e0 1px, transparent 0)",
                      backgroundSize: "20px 20px",
                      flexGrow: 1,
                      paddingBottom: "80px",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#E1BA66",
                        borderRadius: "3px",
                      },
                    }}
                  >
                    {queryError && (
                      <Alert
                        severity="error"
                        sx={{ mb: 2, borderRadius: "8px" }}
                        onClose={() => setQueryError(null)}
                      >
                        {queryError}
                      </Alert>
                    )}
                    {/* Welcome message */}
                    <MessageBubble isUser={false}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Hello! How can I help you today? Please share your
                        question, and our team will get back to you as soon as
                        possible.
                      </Typography>
                    </MessageBubble>
                    {/* Display all messages in the query container */}
                    {queryMessages.map((msg, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <MessageBubble
                          isUser={msg.isUser}
                          sx={{
                            ...(msg.source === "admin" && {
                              borderLeft: "4px solid #4caf50",
                              backgroundColor: msg.isUser
                                ? "#e8f5e9"
                                : "#f5f9f5",
                            }),
                            ...(msg.type === "status-update" && {
                              borderLeft: "4px solid #2196f3",
                              backgroundColor: "#e3f2fd",
                            }),
                            ...(msg.type === "query-confirmation" && {
                              borderLeft: "4px solid #ff9800",
                              backgroundColor: "#fff3e0",
                            }),
                          }}
                        >
                          {/* If it's an admin response, add a header */}
                          {msg.source === "admin" && (
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "#2e7d32",
                                mb: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CheckIcon fontSize="small" />
                              Response from Support Team
                            </Typography>
                          )}

                          {/* If it's a status update, add appropriate header */}
                          {msg.type === "status-update" && (
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "#1565c0",
                                mb: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <AccessTimeIcon fontSize="small" />
                              Status Update
                            </Typography>
                          )}

                          {/* Message text */}
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {msg.text}
                          </Typography>

                          {/* Show status for admin responses */}
                          {(msg.source === "admin" ||
                            msg.type === "status-update") &&
                            msg.status && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 1,
                                  color:
                                    msg.status === "resolved"
                                      ? "#2e7d32"
                                      : msg.status === "in-progress"
                                      ? "#1565c0"
                                      : "#ff9800",
                                  fontWeight: 500,
                                }}
                              >
                                Status:{" "}
                                {msg.status === "resolved"
                                  ? "Resolved"
                                  : msg.status === "in-progress"
                                  ? "In Progress"
                                  : "Open"}
                              </Typography>
                            )}

                          {/* Time */}
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              textAlign: msg.isUser ? "right" : "left",
                              mt: 0.5,
                              color: "text.secondary",
                            }}
                          >
                            {formatTime(msg.timestamp)}
                          </Typography>
                        </MessageBubble>
                      </Box>
                    ))}
                    <div ref={messagesEndRef} />{" "}
                    {/* Reference for scrolling to bottom */}
                  </Box>

                  {/* Fixed input area at the bottom */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: "white",
                      borderTop: "1px solid #e0e0e0",
                      padding: "16px",
                      zIndex: 10,
                      boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.05)",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "1px",
                        background:
                          "linear-gradient(90deg, transparent, #E1BA66, transparent)",
                      },
                    }}
                  >
                    {/* Remove the querySubmitted condition to always show the input */}
                    <Box
                      component="form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleQuerySubmit();
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type your question..."
                        name="question"
                        value={queryFormData.question}
                        onChange={handleQueryInputChange}
                        onKeyPress={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleQuerySubmit();
                          }
                        }}
                        disabled={isLoading}
                        multiline
                        rows={1}
                        maxRows={4} // Allow up to 4 rows of text
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "20px",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E1BA66",
                                borderWidth: "2px",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E1BA66",
                                borderWidth: "2px",
                              },
                            },
                            // Add these styles to handle text expansion
                            "& .MuiInputBase-input": {
                              overflow: "hidden",
                              resize: "none",
                              minHeight: "24px !important",
                              padding: "12px 16px",
                            },
                          },
                          // Add these styles to ensure proper expansion
                          "& .MuiInputBase-root": {
                            minHeight: "48px",
                            alignItems: "flex-start",
                            padding: "4px 8px",
                          },
                        }}
                      />
                      <IconButton
                        color="primary"
                        disabled={
                          isLoading ||
                          !queryFormData.question ||
                          !queryFormData.name ||
                          !queryFormData.email
                        }
                        onClick={handleQuerySubmit}
                        sx={{
                          backgroundColor: "#E1BA66",
                          color: "#2C1810",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            backgroundColor: "#d4a94c",
                            transform: "scale(1.1)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "rgba(225, 186, 102, 0.3)",
                            color: "rgba(44, 24, 16, 0.3)",
                          },
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress
                            size={24}
                            sx={{ color: "#2C1810" }}
                          />
                        ) : (
                          <SendIcon />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              )}
            </ChatWidget>
          )}
        </Box>
      ) : (
        <ChatWidget>
          <ChatHeader>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                textShadow: "0 2px 4px rgba(44, 24, 16, 0.1)",
                animation: "glow 2s ease-in-out infinite",
                "@keyframes glow": {
                  "0%, 100%": {
                    textShadow: "0 2px 4px rgba(44, 24, 16, 0.1)",
                  },
                  "50%": {
                    textShadow: "0 2px 8px rgba(44, 24, 16, 0.2)",
                  },
                },
              }}
            >
              BharatFLP AI Assistant
            </Typography>
            <IconButton
              onClick={() => setIsOpen(false)}
              sx={{
                color: "white",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "rotate(90deg) scale(1.1)",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </ChatHeader>
          <ChatMessages>
            {apiStatus === "invalid" && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
                API Key is invalid. Please check your Google Cloud Console
                settings.
              </Alert>
            )}
            {apiStatus === "error" && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: "8px" }}>
                Unable to verify API Key. Please check your internet connection.
              </Alert>
            )}
            {chatMessages.map((msg, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <MessageBubble
                  isUser={msg.isUser}
                  sx={
                    msg.source === "admin"
                      ? {
                          borderLeft: "4px solid #4caf50",
                          backgroundColor: msg.isUser ? "#e8f5e9" : "#f5f9f5",
                        }
                      : {}
                  }
                >
                  {/* If it's an admin response, add a header */}
                  {msg.source === "admin" && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#2e7d32",
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CheckIcon fontSize="small" />
                      Response from Support Team
                    </Typography>
                  )}

                  {/* Message text */}
                  <Typography variant="body2">{msg.text}</Typography>

                  {/* Show status for admin responses */}
                  {msg.source === "admin" && msg.status && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1,
                        color:
                          msg.status === "resolved" ? "#2e7d32" : "#1565c0",
                      }}
                    >
                      Status: {msg.status}
                    </Typography>
                  )}

                  {/* Time */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: msg.isUser ? "right" : "left",
                      mt: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    {formatTime(msg.timestamp)}
                  </Typography>
                </MessageBubble>

                {/* Multiple suggestions after bot reply */}
                {!msg.isUser &&
                  index === chatMessages.length - 1 &&
                  showSuggestions &&
                  suggestedQuestions.length > 0 && (
                    <SuggestionsContainer>
                      {suggestedQuestions.map((question, idx) => (
                        <Tooltip key={idx} title="Click to get answer">
                          <SuggestionChip
                            onClick={() => handleSuggestionClick(question)}
                          >
                            {question.length > 30
                              ? `${question.substring(0, 27)}...`
                              : question}
                          </SuggestionChip>
                        </Tooltip>
                      ))}
                    </SuggestionsContainer>
                  )}
              </Box>
            ))}
            {isTyping && (
              <TypingIndicator>
                <Dot delay={0} />
                <Dot delay={200} />
                <Dot delay={400} />
              </TypingIndicator>
            )}
            {isLoading && !isTyping && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress size={24} sx={{ color: "#E1BA66" }} />
              </Box>
            )}
            <div ref={messagesEndRef} />
          </ChatMessages>
          <ChatInput>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <Tooltip title="Attach file">
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  color: "#E1BA66",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "scale(1.1) rotate(10deg)",
                    backgroundColor: "rgba(225, 186, 102, 0.1)",
                  },
                }}
                disabled={isLoading}
              >
                <AttachFileIcon />
              </IconButton>
            </Tooltip>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              multiline
              maxRows={4}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E1BA66",
                      borderWidth: "2px",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E1BA66",
                      borderWidth: "2px",
                    },
                    transform: "scale(1.01)",
                  },
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={isLoading || (!input.trim() && !selectedFile)}
              sx={{
                color: "#E1BA66",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "scale(1.1) rotate(-10deg)",
                  backgroundColor: "rgba(225, 186, 102, 0.1)",
                },
                "&.Mui-disabled": {
                  color: "rgba(225, 186, 102, 0.3)",
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </ChatInput>
        </ChatWidget>
      )}
    </ChatContainer>
  );
};

export default Chatbot;
